import { database } from '../firebase';
import { ref, push, onValue, remove, set, get } from 'firebase/database';

/**
 * Service pour gérer les adresses de covoiturage personnalisées
 * Stockées dans Firebase sous 'carpoolLocations'
 */

/**
 * Écouter toutes les adresses de covoiturage
 */
export function listenToCarpoolLocations(callback) {
  try {
    const locationsRef = ref(database, 'carpoolLocations');
    
    const unsubscribe = onValue(locationsRef, (snapshot) => {
      if (snapshot.exists()) {
        const locations = [];
        snapshot.forEach((childSnapshot) => {
          locations.push({
            id: childSnapshot.key,
            ...childSnapshot.val(),
          });
        });
        callback(locations);
      } else {
        callback([]);
      }
    }, (error) => {
      console.error('❌ [carpoolLocationsService] Erreur listener:', error);
      callback([]);
    });

    return unsubscribe;
  } catch (error) {
    console.error('❌ [carpoolLocationsService] Erreur setup listener:', error);
    return () => {};
  }
}

/**
 * Ajouter une nouvelle adresse de covoiturage
 */
export async function addCarpoolLocation(locationData) {
  try {
    const { name, address, city, postalCode, latitude, longitude } = locationData;
    
    if (!name || !city || !postalCode) {
      throw new Error('Le nom, la ville et le code postal sont requis');
    }

    // Si pas d'adresse mais des coordonnées GPS, utiliser les coordonnées
    const finalAddress = address || (latitude && longitude ? `${latitude},${longitude}` : '');
    
    if (!finalAddress && !latitude && !longitude) {
      throw new Error('Une adresse ou des coordonnées GPS sont requises');
    }

    const locationsRef = ref(database, 'carpoolLocations');
    const newLocationRef = await push(locationsRef, {
      name: name.trim(),
      address: finalAddress,
      city: city.trim(),
      postalCode: postalCode.trim(),
      latitude: latitude ? parseFloat(latitude) : null,
      longitude: longitude ? parseFloat(longitude) : null,
      createdAt: Date.now(),
    });

    console.log('✅ [carpoolLocationsService] Adresse ajoutée:', newLocationRef.key);
    return { success: true, id: newLocationRef.key };
  } catch (error) {
    console.error('❌ [carpoolLocationsService] Erreur ajout:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Supprimer une adresse de covoiturage
 */
export async function deleteCarpoolLocation(locationId) {
  try {
    const locationRef = ref(database, `carpoolLocations/${locationId}`);
    await remove(locationRef);
    
    console.log('✅ [carpoolLocationsService] Adresse supprimée:', locationId);
    return { success: true };
  } catch (error) {
    console.error('❌ [carpoolLocationsService] Erreur suppression:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Récupérer toutes les adresses de covoiturage (pour le composant)
 */
export async function getCarpoolLocations() {
  try {
    const locationsRef = ref(database, 'carpoolLocations');
    const snapshot = await get(locationsRef);
    
    if (snapshot.exists()) {
      const locations = [];
      snapshot.forEach((childSnapshot) => {
        locations.push({
          id: childSnapshot.key,
          ...childSnapshot.val(),
        });
      });
      return locations;
    }
    
    return [];
  } catch (error) {
    console.error('❌ [carpoolLocationsService] Erreur récupération:', error);
    return [];
  }
}

/**
 * Mettre à jour une adresse de covoiturage
 */
export async function updateCarpoolLocation(locationId, locationData) {
  try {
    const { name, address, city, postalCode, latitude, longitude } = locationData;
    
    if (!name || !city || !postalCode) {
      throw new Error('Le nom, la ville et le code postal sont requis');
    }

    const finalAddress = address || (latitude && longitude ? `${latitude},${longitude}` : '');
    
    if (!finalAddress && !latitude && !longitude) {
      throw new Error('Une adresse ou des coordonnées GPS sont requises');
    }

    const locationRef = ref(database, `carpoolLocations/${locationId}`);
    await set(locationRef, {
      name: name.trim(),
      address: finalAddress,
      city: city.trim(),
      postalCode: postalCode.trim(),
      latitude: latitude ? parseFloat(latitude) : null,
      longitude: longitude ? parseFloat(longitude) : null,
      createdAt: Date.now(), // Conserver la date de création originale si nécessaire
    });

    console.log('✅ [carpoolLocationsService] Adresse mise à jour:', locationId);
    return { success: true };
  } catch (error) {
    console.error('❌ [carpoolLocationsService] Erreur mise à jour:', error);
    return { success: false, error: error.message };
  }
}
